import { PolicyFilterBase } from '@warebee/shared/common';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { POLICY_FILTER_VALUES_PAGE_SIZE } from '../../common/constants';
import { AsyncLoadStatus } from '../../common/types';
import ButtonSwitchMulti, {
  ButtonSwitchMultiOption,
} from '../../components/actions/ButtonSwitchMulti';
import DropdownSelector from '../../components/actions/DropdownSelector';
import { Spacer } from '../../components/layout/Spacer';
import { ActionBar } from '../../components/nav/ActionBar';
import { policyFilterData } from '../../simulation/store/policyFilter.state';
import { getPolicyFilterKey } from '../policyFilter.helper';
import {
  PolicyFilterEditorData,
  PolicyFilterEditorEvents,
} from '../policyFilter.types';
import PolicyEditorContainer from './PolicyEditorContainer';
import PolicyEditorDataStatus from './PolicyEditorDataStatus';
import PolicyEditorList from './PolicyEditorList';
import PolicyStringRangeEditor from './PolicyStringRangeEditor';

export type PolicyFilterStringEditorProps = PolicyFilterEditorData &
  PolicyFilterEditorEvents & {
    mode: 'items' | 'range' | 'both';
    selectAllThreshold?: number;
  };

const PolicyFilterStringEditor: React.FC<
  PolicyFilterStringEditorProps
> = props => {
  const { t } = useTranslation('app');
  const selectAllThreshold = props.selectAllThreshold ?? 10;

  const filterKey = getPolicyFilterKey(props.filterType, 'main');
  const filterData = useRecoilValue(policyFilterData(filterKey));
  const isLoading = filterData?.status === AsyncLoadStatus.Loading;
  const loadedCount = filterData?.content?.length || 0;
  const totalCount = filterData?.totalCount || 0;
  const availableItems = filterData?.content || [];
  const selectedItems = props.value?.valueIn || [];
  const selectedRangeFilters =
    props.value && !_.isNil(props.value?.stringRange);
  const showRangeFilters =
    (props.mode === 'both' && selectedRangeFilters) || props.mode === 'range';
  const isNotFilter = props.value?.isNot;
  const modes: ButtonSwitchMultiOption[] = [
    { label: t`Item` },
    { label: t`Range` },
  ];
  const excludeModes: ButtonSwitchMultiOption[] = [
    { label: t`Include` },
    { label: t`Exclude` },
  ];

  // Calculate selection states first
  const allLoadedItemsSelected =
    selectedItems.length === availableItems.length && availableItems.length > 0;
  const hasMoreItemsToLoad = loadedCount < totalCount;

  // Select all/deselect all functionality
  const showSelectAllButtons = (() => {
    if (showRangeFilters || totalCount < selectAllThreshold) return false;

    // Check if any options would be available
    const hasSelectOption = !allLoadedItemsSelected;
    const hasDeselectOption = selectedItems.length > 0;
    const hasLoadOptions = hasMoreItemsToLoad;

    return hasSelectOption || hasDeselectOption || hasLoadOptions;
  })();

  function handleSelectLoadedItems() {
    const newValue: PolicyFilterBase<string> = {
      ...props.value,
      type: props.filterType,
      valueIn: availableItems,
      isNot: isNotFilter,
    };
    props.onChange(newValue);
  }

  function handleDeselectAll() {
    const newValue: PolicyFilterBase<string> = {
      ...props.value,
      type: props.filterType,
      valueIn: [],
      isNot: isNotFilter,
    };
    props.onChange(newValue);
  }

  function handleLoadNext() {
    if (hasMoreItemsToLoad) {
      props.onLoadNext(filterKey, loadedCount);
    }
  }

  function handleLoadAll() {
    if (hasMoreItemsToLoad) {
      // Use the dedicated onLoadAll function to load all remaining data
      props.onLoadAll(filterKey);
    }
  }

  function onModeChange(modeIndex: number) {
    const newValue: PolicyFilterBase<string> = {
      type: props.filterType,
      valueIn: modeIndex === 0 ? [] : null,
      isNot: isNotFilter,
      stringRange:
        modeIndex === 0
          ? null
          : {
              from: null,
              to: null,
            },
    };
    props.onChange(newValue);
  }
  const showSummary = !showRangeFilters;

  const drawSummary = () => (
    <>
      {/* {props.mode === 'both' && (
        <ButtonSwitchMulti
          buttonType="minimal"
          autoSize
          className="my-1"
          options={modes}
          selectedIndex={showRangeFilters ? 1 : 0}
          onClick={onModeChange}
        />
      )} */}

      {showSummary && (
        <PolicyEditorDataStatus
          totalCount={totalCount}
          loadedCount={loadedCount}
        />
      )}
    </>
  );

  return (
    <div data-component="PolicyFilterEditorSelect">
      <PolicyEditorContainer
        title={props.title}
        filterType={props.filterType}
        summary={drawSummary()}
        summaryMode={showSummary ? 'always' : 'open'}
        isLoading={isLoading}
      >
        <ActionBar className="px-1 py-2">
          <ButtonSwitchMulti
            buttonType="minimal"
            autoSize
            options={excludeModes}
            selectedIndex={isNotFilter ? 1 : 0}
            onClick={index =>
              props.onChange({
                ...props.value,
                type: props.filterType,
                isNot: index === 1,
              })
            }
          />

          {props.mode === 'both' && (
            <>
              <Spacer flexspace />
              <ButtonSwitchMulti
                buttonType="minimal"
                autoSize
                options={modes}
                selectedIndex={showRangeFilters ? 1 : 0}
                onClick={onModeChange}
              />
            </>
          )}
          {showSelectAllButtons && (
            <>
              <Spacer flexspace />
              <DropdownSelector
                buttonTransparent
                w_lg
                // policyMode
                vertical
                DropAlignRight
                values={
                  [
                    // Show select option only if not all loaded items are selected
                    !allLoadedItemsSelected && 'selectLoaded',
                    // Show deselect option only if items are selected
                    selectedItems.length > 0 && 'deselectAll',
                    // Show load next option only if more data is available
                    hasMoreItemsToLoad && 'loadNext',
                    // Show load all option only if more data is available
                    hasMoreItemsToLoad && 'loadAll',
                  ].filter(Boolean) as string[]
                }
                value={
                  // Determine the most appropriate default value from available options
                  (() => {
                    const availableOptions = [
                      !allLoadedItemsSelected && 'selectLoaded',
                      selectedItems.length > 0 && 'deselectAll',
                      hasMoreItemsToLoad && 'loadNext',
                      hasMoreItemsToLoad && 'loadAll',
                    ].filter(Boolean) as string[];

                    if (availableOptions.length === 0) return '';

                    // Prefer select option if available and no items selected
                    if (
                      selectedItems.length === 0 &&
                      availableOptions.includes('selectLoaded')
                    ) {
                      return 'selectLoaded';
                    }

                    // Prefer deselect option if available and items are selected
                    if (
                      selectedItems.length > 0 &&
                      availableOptions.includes('deselectAll')
                    ) {
                      return 'deselectAll';
                    }

                    // Default to first available option
                    return availableOptions[0];
                  })()
                }
                renderValue={key => {
                  if (key === 'selectLoaded') {
                    const label = hasMoreItemsToLoad
                      ? `${t`Select Loaded`} (${availableItems.length}/${totalCount})`
                      : `${t`Select All`} (${availableItems.length})`;
                    return label;
                  }
                  if (key === 'deselectAll') {
                    return `${t`Deselect All`} (${selectedItems.length})`;
                  }
                  if (key === 'loadNext') {
                    const itemsToLoad = Math.min(
                      POLICY_FILTER_VALUES_PAGE_SIZE,
                      totalCount - loadedCount,
                    );
                    return `${t`Load Next`} ${itemsToLoad} (${totalCount - loadedCount} ${t`remaining`})`;
                  }
                  if (key === 'loadAll') {
                    return `${t`Load All`} (${totalCount - loadedCount} ${t`remaining`})`;
                  }
                  return key;
                }}
                onChange={key => {
                  if (key === 'selectLoaded') {
                    handleSelectLoadedItems();
                  } else if (key === 'deselectAll') {
                    handleDeselectAll();
                  } else if (key === 'loadNext') {
                    handleLoadNext();
                  } else if (key === 'loadAll') {
                    handleLoadAll();
                  }
                }}
              />
            </>
          )}
        </ActionBar>
        {!showRangeFilters && (
          <PolicyEditorList {...props} isNot={isNotFilter} />
        )}
        {showRangeFilters && <PolicyStringRangeEditor {...props} />}
      </PolicyEditorContainer>
    </div>
  );
};

export default PolicyFilterStringEditor;
